import React, { useState, useRef } from 'react';
import { DrawPad } from './DrawPad';
import { Upload, FileCheck, AlertCircle } from 'lucide-react';

const input = 'w-full border border-gray-400 rounded px-3 py-2 text-sm';
const COLLATERAL = [['category', 'Collateral category (e.g. identity documents, vital records)'], ['description', 'Detailed description of collateral'],
  ['existing_liens', 'Existing liens and priority agreements'], ['proceeds_included', 'Proceeds included'],
  ['excluded_property', 'Excluded property'], ['jurisdiction', 'Governing jurisdiction for perfection review']];

const DocumentUpload = ({ label, description, value, onChange, testId }) => {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('File must be under 5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="border border-gray-300 rounded p-3 bg-gray-50">
      <p className="text-sm font-medium text-gray-700 mb-1">{label} <span className="text-gray-400 text-xs">(optional now, can upload later)</span></p>
      <p className="text-xs text-gray-500 mb-2">{description}</p>
      <input type="file" ref={inputRef} onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid={testId} />
      <button type="button" onClick={() => inputRef.current?.click()}
        className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${value ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
        {value ? <FileCheck className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
        {value ? `Uploaded: ${fileName || 'Document'}` : 'Upload document (PDF or image, max 5MB)'}
      </button>
    </div>
  );
};

export const AgreementSignForm = ({ defaults, busy, onSign, collateralExempt }) => {
  const [ink, setInk] = useState(null);
  const [f, setF] = useState({ signer_name: defaults.legal_name || '', address: defaults.address || '', jurisdiction: defaults.jurisdiction || '', entity_title: '', initials: '' });
  const [col, setCol] = useState({ category: 'Identity Documents and Vital Records', description: 'Certificate of Live Birth/Birth Certificate and Social Security Card as pledged collateral' });
  const [birthCert, setBirthCert] = useState('');
  const [ssCard, setSSCard] = useState('');
  const [accept, setAccept] = useState(false);
  const [acceptNote, setAcceptNote] = useState(false);
  const [acceptCollateral, setAcceptCollateral] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  
  const ready = accept && acceptNote && ink && (collateralExempt || acceptCollateral)
    && f.signer_name.trim().length >= 3 && f.address.trim().length >= 5 && f.jurisdiction.trim().length >= 2
    && f.initials.trim().length >= 2;
  
  const submit = () => onSign({ 
    ...f, 
    image_base64: ink, 
    accept, 
    accept_note: acceptNote, 
    collateral: { 
      ...col, 
      birth_certificate_base64: birthCert,
      social_security_card_base64: ssCard,
      accept_collateral_terms: acceptCollateral
    } 
  });
  
  return (
    <div className="font-sans space-y-4 print:hidden" data-testid="agreement-sign-form">
      {collateralExempt ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800" data-testid="collateral-exempt-notice">
          Administrator account: collateral document uploads (Birth Certificate / Social Security Card) are not required.
        </div>
      ) : (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="collateral-notice">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Pledged Collateral Documents</p>
            <p className="text-xs text-amber-700 mt-1">
              As required by Article VI of this Agreement, you pledge your Certificate of Live Birth/Birth Certificate 
              and Social Security Card as collateral for the acquisition and purchase of Treasury Securities under this Treasury Contract.
              You may upload them now or later from your Banking page; signing is not delayed.
            </p>
          </div>
        </div>
      </div>
      )}
      
      <input value={f.signer_name} onChange={set('signer_name')} placeholder="Full legal name" data-testid="agreement-signer-name" className={input} />
      <input value={f.address} onChange={set('address')} placeholder="Address" data-testid="agreement-signer-address" className={input} />
      <div className="grid grid-cols-2 gap-2">
        <input value={f.jurisdiction} onChange={set('jurisdiction')} placeholder="Jurisdiction (state)" data-testid="agreement-signer-jurisdiction" className={input} />
        <input value={f.entity_title} onChange={set('entity_title')} placeholder="Entity title, if applicable" data-testid="agreement-signer-entity-title" className={input} />
      </div>
      
      {!collateralExempt && <div className="border border-gray-300 rounded-lg p-4 space-y-4 bg-white" data-testid="agreement-collateral-section">
        <h4 className="font-semibold text-gray-800">Schedule A — Collateral Documents</h4>
        
        <DocumentUpload 
          label="Certificate of Live Birth / Birth Certificate"
          description="Upload a clear copy of your birth certificate as pledged collateral"
          value={birthCert}
          onChange={setBirthCert}
          testId="collateral-birth-cert"
        />
        
        <DocumentUpload 
          label="Social Security Card"
          description="Upload a clear copy of your Social Security Card as pledged collateral"
          value={ssCard}
          onChange={setSSCard}
          testId="collateral-ss-card"
        />
        
        <div className="space-y-2 pt-2 border-t border-gray-200">
          {COLLATERAL.map(([k, ph]) => (
            <input key={k} value={col[k] || ''} onChange={(e) => setCol({ ...col, [k]: e.target.value })} 
              placeholder={ph} data-testid={`schedule-a-${k}`} className={input} />
          ))}
        </div>
      </div>}
      
      <DrawPad onChange={setInk} />
      <label className="block text-xs text-gray-600">Initials (Schedule A)
        <input value={f.initials} onChange={(e) => setF({ ...f, initials: e.target.value.toUpperCase().slice(0, 4) })} placeholder="e.g. JD" data-testid="agreement-signer-initials" className="block mt-1 w-28 border border-gray-400 rounded px-3 py-2 text-sm" />
      </label>
      
      <div className="space-y-3 border-t border-gray-200 pt-4">
        {!collateralExempt && <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={acceptCollateral} onChange={(e) => setAcceptCollateral(e.target.checked)} data-testid="agreement-accept-collateral-checkbox" className="mt-1" />
          <span>
            I agree to upload and pledge my Certificate of Live Birth/Birth Certificate and Social Security Card as collateral for the acquisition and purchase 
            of Treasury Securities, T-Bills, T-Notes, T-Bonds, and reopening CUSIPs under this Treasury Contract. I understand these securities and derived instruments 
            may be sold, transferred, assigned, discounted, pledged, hypothecated, rehypothecated, leveraged, bartered, allocated, or margined in the form of 
            BETH Coin Exchange Market crypto coins, digital assets, Treasury Checks, bills, assignments, Series A through F Certificates, or other obligations, 
            without recourse and as a priority perfected security interest, including credit swaps and default swaps.
          </span>
        </label>}
        
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="agreement-accept-checkbox" className="mt-1" />
          I have read and agree to the BETH Exchange Market Subscriber Credit, Digital Payment, Collateral and Receivables Agreement, including Schedules A and B.
        </label>
        
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={acceptNote} onChange={(e) => setAcceptNote(e.target.checked)} data-testid="agreement-accept-note-checkbox" className="mt-1" />
          I sign the USD-Denominated Promissory Note: 20-year term, 1% per annum interest (actual/365), interest-only monthly payments, repaid in BETH at 1 BETH = $1.00 USD by automatic debit from my BETH wallet to the BETHCOIN Exchange Market Treasury Reserve Wallet.
        </label>
      </div>
      
      <button onClick={submit} disabled={busy || !ready} data-testid="agreement-sign-btn"
        className="w-full px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold disabled:opacity-40">
        {busy ? 'Signing...' : 'Sign Agreement, Promissory Note & Collateral Pledge'}
      </button>
      
      {!ready && (
        <p className="text-xs text-gray-500 text-center">
          Complete all required fields, draw your signature, and accept all terms to sign. Collateral documents can be uploaded later.
        </p>
      )}
    </div>
  );
};
